import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Reservation {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  roomId: string;
  roomName: string;
  checkIn: string;
  checkOut: string;
  guests: number;
  specialRequests: string;
  created: string;
  totalPrice: number;
  status: 'confirmed' | 'cancelled' | 'completed';
}

interface ReservationContextType {
  reservations: Reservation[];
  makeReservation: (reservation: Reservation) => void;
  cancelReservation: (id: string) => void;
  exportToExcel: () => void;
}

const ReservationContext = createContext<ReservationContextType | undefined>(undefined);

export const useReservation = () => {
  const context = useContext(ReservationContext);
  if (context === undefined) {
    throw new Error('useReservation must be used within a ReservationProvider');
  }
  return context;
};

interface ReservationProviderProps {
  children: ReactNode;
}

export const ReservationProvider: React.FC<ReservationProviderProps> = ({ children }) => {
  const [reservations, setReservations] = useState<Reservation[]>(() => {
    const savedReservations = localStorage.getItem('guestHouseReservations');
    return savedReservations ? JSON.parse(savedReservations) : [];
  });

  useEffect(() => {
    localStorage.setItem('guestHouseReservations', JSON.stringify(reservations));
  }, [reservations]);

  const makeReservation = (reservation: Reservation) => {
    // Save reservation locally
    setReservations(prev => [...prev, reservation]);

    // Notify backend to send an email
    fetch('http://localhost:3001/api/send-reservation', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(reservation),
    }).catch(error => {
      console.error('Eroare trimitere email:', error);
    });
  };

  const cancelReservation = (id: string) => {
    setReservations(prev =>
        prev.map(reservation =>
            reservation.id === id
                ? { ...reservation, status: 'cancelled' }
                : reservation
        )
    );
  };

  const exportToExcel = async () => {
    try {
      const response = await fetch('http://localhost:3001/api/export-reservations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reservations }),
      });

      if (response.ok) {
        alert('Fișierul Excel a fost trimis pe email!');
      } else {
        throw new Error('Eroare la trimiterea emailului');
      }
    } catch (error) {
      console.error('Eroare:', error);
      alert('Nu am putut trimite fișierul. Încearcă din nou.');
    }
  };

  const value = {
    reservations,
    makeReservation,
    cancelReservation,
    exportToExcel,
  };

  return (
      <ReservationContext.Provider value={value}>
        {children}
      </ReservationContext.Provider>
  );
};
